import React, { isValidElement } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import classNames from "classnames";
import "components/page/page.scss";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import PageActions, {
  IActionDetails,
  IMultipleActionDropdownPageAction,
  IPrimaryPageAction,
  ISecondaryPageAction,
  ITextAction,
} from "components/page/PageActions";

import ButtonGroup from "components/button/ButtonGroup";
import { ButtonNew as Button } from "components/buttonNew";
import { INotificationBarProps } from "components/notificationBar/NotificationBar";
import { NavigationDropdown } from "components/navigationDropdown/NavigationDropdown";
import Toast from "components/toast/Toast";
import { ISplitButtonProps } from "components/splitButton/SplitButton";

interface IPageProps {
  id?: string;
  className?: string;
  full?: boolean;
  /** Fill the admin main content area for viewport-height layouts (calendars, sticky tables). */
  fillViewport?: boolean;
  narrow?: boolean;
  breadcrumbs?: any;
  thumbnail?: any;
  title?: string | React.JSX.Element;
  titleMetadata?: any;
  subtitle?: string | React.JSX.Element;
  primaryAction?: IPrimaryPageAction;
  secondaryActions?: ISecondaryPageAction[];
  multipleActionDropdownAction?: IMultipleActionDropdownPageAction;
  subActions?: any;
  splitLayout?: boolean;
  splitButtonAction?: ISplitButtonProps;
  notificationBarProps?: INotificationBarProps;
  breadcrumbsNavigateParams?: Record<string, any>;
  actionDetails?: IActionDetails;
  headerContent?: React.ReactNode;
  /** Renders below the page header (outside the header row). Use for page-specific banners. */
  belowHeaderContent?: React.ReactNode;
  textAction?: ITextAction;
}

const Section = (props: any) => (
  <div
    className={classNames("ui-page-section", {
      "ui-page-section_one-third": props.oneThird,
      "ui-page-section_two-thirds": props.twoThirds,
    })}
  >
    {props.children}
  </div>
);

const Page = (props: IPageProps & { children?: any }) => {
  const history = useHistory();
  const { t } = useTranslation();

  function breadCrumbsNavigation(url: string) {
    history.push(url, { params: props?.breadcrumbsNavigateParams });
  }

  return (
    <div
      id={props.id}
      className={classNames("ui-page", {
        "ui-page_full": props.full,
        "ui-page_fill-viewport": props.fillViewport,
        "ui-page_narrow": props.narrow,
        [props.className]: props.className,
      })}
    >
      {props.title || props.multipleActionDropdownAction || props.breadcrumbs ? (
        <div
          className={classNames("ui-page-header ui-page-header_medium-title", {
            "ui-page-header_no-breadcrumbs": !props.breadcrumbs,
          })}
        >
          <div className="ui-page-header_row">
            {props.breadcrumbs ? (
              <div className="ui-page-header_breadcrumb-wrapper">
                {props.breadcrumbs.map((breadcrumb: any, index: number) => {
                  const isLast = index === props.breadcrumbs.length - 1;
                  const isClickable = Boolean(breadcrumb.url) && (props.breadcrumbs.length === 1 || !isLast);
                  return (
                    <React.Fragment key={index}>
                      <div
                        className={classNames("ui-breadcrumb-item", {
                          "ui-breadcrumb-active": isLast && props.breadcrumbs.length > 1,
                          "ui-breadcrumb-static": !isClickable && !(isLast && props.breadcrumbs.length > 1),
                        })}
                        onClick={() => {
                          if (isClickable) {
                            breadCrumbsNavigation(String(breadcrumb.url));
                          }
                        }}
                      >
                        {breadcrumb.prefix ? (
                          <FontAwesomeIcon icon={["far", "arrow-left"]} className="mr-2 text-gray-400 text-sm" />
                        ) : null}
                        <span>{breadcrumb.label}</span>
                      </div>
                      {!isLast && (
                        <div className="ui-breadcrumb-separator">
                          <FontAwesomeIcon icon={["far", "chevron-right"]} style={{ height: "100%" }} />
                        </div>
                      )}
                    </React.Fragment>
                  );
                })}
              </div>
            ) : null}

            {props.title || props.subtitle || props.thumbnail || props.titleMetadata ? (
              <div className="ui-page-header_title-wrapper">
                <div
                  className={classNames("ui-header-title", {
                    "ui-header-title-thumbnail": props.thumbnail,
                  })}
                >
                  {props.thumbnail ? <>{props.thumbnail}</> : null}

                  <div>
                    <div className="flex flex-wrap items-end display-sm text-semibold">
                      {isValidElement(props.title) ? props.title : <h1>{props.title}</h1>}
                      <div className="text-xl text-gray ml-3.5 mb-1.5">{props.titleMetadata}</div>
                    </div>

                    {props.subtitle ? (
                      <div className="ui-page-header_subtitle">
                        {isValidElement(props.subtitle) ? props.subtitle : <p className="text-xl">{props.subtitle}</p>}
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>
            ) : null}
            {props.primaryAction ||
            props.secondaryActions ||
            props.multipleActionDropdownAction ||
            props.splitButtonAction ||
            props.actionDetails ||
            props.textAction ? (
              <div className="ui-page-header_right-aligned">
                <div className="w-full flex justify-end items-center gap-3 my-auto">
                  {props.headerContent ? props.headerContent : null}
                  <PageActions
                    primaryAction={props.primaryAction}
                    secondaryActions={props.secondaryActions}
                    multipleActionDropdownAction={props.multipleActionDropdownAction}
                    splitButtonAction={props.splitButtonAction}
                    actionDetails={props.actionDetails}
                    textAction={props.textAction}
                  />
                </div>
              </div>
            ) : props.headerContent ? (
              props.headerContent
            ) : null}
          </div>

          <div className="ui-page-sub-actions">
            <ButtonGroup>
              {props.subActions &&
                props.subActions.map((action: any, index: number) => {
                  if (action.type === "dropdown") {
                    return (
                      <Button type="tertiary" key={index}>
                        <NavigationDropdown showPlainTextLabel label={action.label} options={action.options} />
                      </Button>
                    );
                  } else {
                    return (
                      <Button onClick={action.onClick} type={action.type} key={index}>
                        {action.label}
                      </Button>
                    );
                  }
                })}
            </ButtonGroup>
          </div>

          {props?.belowHeaderContent ? (
            <div className="ui-page-below-header">{props.belowHeaderContent}</div>
          ) : null}
        </div>
      ) : null}

      <div
        className={classNames("ui-page_content", {
          "ui-page_split-layout": props.splitLayout,
        })}
      >
        {React.Children?.map(props.children, child => {
          return <>{child}</>;
        })}
      </div>

      {props.notificationBarProps ? (
        <Toast
          mounted={props.notificationBarProps.isVisible}
          title={t("Careful, you have unsaved changes on this page!")}
          type={"warning"}
          onClose={props.notificationBarProps.onCancel}
          onSave={props.notificationBarProps.onAction}
        />
      ) : null}
    </div>
  );
};

Section.displayName = "Section";
Page.Section = Section;

export default Page;
